// src/components/NotificationProfileRequestBadgeIcon.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { API_URL } from "config";

/* ───── Global singleton (safe in HMR) ───── */
const __g = typeof window !== "undefined" ? window : globalThis;
if (!__g.__profileReqSingleton) {
  __g.__profileReqSingleton = {
    counter: 0,
    listeners: new Set(),
    loaded: false,
    loading: false,
  };
}
if (!__g.__profileReqAudio) {
  // single shared Audio element
  __g.__profileReqAudio = new Audio("https://actions.google.com/sounds/v1/alarms/alarm_clock.ogg");
}

/* ───── Shared singleton poller (10s) ───── */
let __profileReq_counter = __g.__profileReqSingleton.counter;
let __profileReq_listeners = __g.__profileReqSingleton.listeners;

// const __startProfileReqPolling = () => {
//   if (__g.__profileReqSingleton.intervalId) return;

//   const token = localStorage.getItem("authToken") || "";
//   const headers = { Authorization: `Bearer ${token}` };

//   const publish = (val) => {
//     if (val !== __profileReq_counter) {
//       __profileReq_counter = val;
//       __g.__profileReqSingleton.counter = val;
//       __profileReq_listeners.forEach((fn) => { try { fn(val); } catch {} });
//     }
//   };

//   const fetchOnce = async () => {
//     try {
//       const res = await axios.get(
//         `${API_URL}/admin/profile-update-requests?_=${Date.now()}`,
//         { headers }
//       );
//       const arr = Array.isArray(res?.data?.requests) ? res.data.requests : [];
//       publish(arr.length);
//     } catch {
//       // optional fallback
//       try {
//         const resCount = await axios.get(
//           `${API_URL}/admin/profile-update-notifications/count?_=${Date.now()}`,
//           { headers }
//         );
//         const raw = resCount?.data?.count;
//         const fallback = typeof raw === "number" ? raw : parseInt(raw, 10) || 0;
//         publish(fallback);
//       } catch {
//         // keep last value
//       }
//     }
//   };

//   fetchOnce();
//   __g.__profileReqSingleton.intervalId = setInterval(fetchOnce, 10000);
// };

const __startProfileReqPolling = () => {
  if (__g.__profileReqSingleton.loaded || __g.__profileReqSingleton.loading) return;
  __g.__profileReqSingleton.loading = true;

  const token = localStorage.getItem("authToken") || "";
  const headers = { Authorization: `Bearer ${token}` };

  const publish = (val) => {
    if (val !== __profileReq_counter) {
      __profileReq_counter = val;
      __g.__profileReqSingleton.counter = val;
      __profileReq_listeners.forEach((fn) => {
        try { fn(val); } catch { }
      });
    }
  };

  const fetchOnce = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/admin/profile-update-requests?_=${Date.now()}`,
        { headers }
      );
      const arr = Array.isArray(res?.data?.requests) ? res.data.requests : [];
      publish(arr.length);
    } catch {
      try {
        const resCount = await axios.get(
          `${API_URL}/admin/profile-update-notifications/count?_=${Date.now()}`,
          { headers }
        );
        const raw = resCount?.data?.count;
        publish(typeof raw === "number" ? raw : parseInt(raw, 10) || 0);
      } catch { }
    } finally {
      __g.__profileReqSingleton.loaded = true;
      __g.__profileReqSingleton.loading = false;
    }
  };

  // ✅ ONLY ONE CALL
  fetchOnce();
};


const __subscribeProfileReq = (fn) => {
  __profileReq_listeners.add(fn);
  try { fn(__g.__profileReqSingleton.counter); } catch { }
  __startProfileReqPolling();
  return () => __profileReq_listeners.delete(fn);
};

/* ───── Parent badge (NO ICON): loop sound while count > 0 ───── */
const NotificationProfileRequestBadgeIcon = ({ playSound, boxSize }) => {
  const [count, setCount] = useState(__g.__profileReqSingleton.counter);

  const audio = useMemo(() => __g.__profileReqAudio, []);
  const unsubRef = useRef(null);

  useEffect(() => {
    unsubRef.current = __subscribeProfileReq((newCount) => {
      setCount(newCount);

      if (!playSound) return;

      if (newCount > 0) {
        try {
          audio.loop = true;
          if (audio.paused) audio.play().catch(() => { });
        } catch { }
      } else {
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.loop = false;
        } catch { }
      }
    });

    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, [audio, playSound]);

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        width: boxSize,
        height: boxSize,
      }}
    >
      {/* No base icon — only badge when count > 0 */}
      {count > 0 && (
        <span
          style={{
            position: "absolute",
            top: -2,
            right: -2,
            minWidth: 16,
            height: 16,
            padding: "0 6px",
            borderRadius: 999,
            background: "#e53935",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: "16px",
            textAlign: "center",
            animation: "pulse 1s infinite",
          }}
          aria-label={`Pending requests: ${count}`}
          title={`${count}`}
        >
          {count}
        </span>
      )}
      <style>
        {`@keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }`}
      </style>
    </div>
  );
};

NotificationProfileRequestBadgeIcon.propTypes = {
  playSound: PropTypes.bool,
  boxSize: PropTypes.number,
};

NotificationProfileRequestBadgeIcon.defaultProps = {
  playSound: true,  // parent = sound ON
  boxSize: 20,      // container size so badge position stable rahe
};

/* ───── Child inline badge (silent count only) ───── */
export const ProfileRequestInlineBadge = () => {
  const [count, setCount] = useState(__g.__profileReqSingleton.counter);

  useEffect(() => {
    const off = __subscribeProfileReq((newCount) => setCount(newCount));
    return () => off();
  }, []);

  if (count <= 0) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 16,
        height: 16,
        padding: "0 6px",
        fontSize: 10,
        lineHeight: "16px",
        borderRadius: 8,
        background: "#e53935",
        color: "#fff",
        fontWeight: 600,
        marginLeft: 6,
      }}
      aria-label={`Pending requests: ${count}`}
      title={`${count}`}
    >
      {count}
    </span>
  );
};

export default NotificationProfileRequestBadgeIcon;
